import type { Campaign, OrderLine } from '../types'
import { formatTRY } from './money'

/**
 * Kampanya hesabı (14 Eki 2026).
 *
 * Kasa adisyona bakıp hangi kampanyaların uyduğunu ve her birinin ne kadar
 * düşüreceğini soruyor; kasiyer birini seçerse o tutar ödenecekten düşüyor.
 * Kendiliğinden uygulanmıyor (kullanıcı kararı). Birden çok kampanya
 * eklenebilir ama aynı ürüne iki kampanya uygulanmaz: eklenen kampanyanın
 * kapsadığı satırlar sonrakilerin hesabından çıkıyor (stackCampaigns).
 *
 * Saf fonksiyonlar: saat ve ürünün kategorisi dışarıdan veriliyor, testler
 * gerçek saate bağlı kalmasın diye.
 */

export interface CampaignOffer {
  campaign: Campaign
  kurus: number
}

const minutesOf = (hhmm: string | undefined): number | null => {
  if (!hhmm) return null
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim())
  if (!m) return null
  return Number(m[1]) * 60 + Number(m[2])
}

const localDay = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

/** Kampanya şu an geçerli mi: açık mı, tarih, gün ve saat aralığında mı. */
export function isCampaignLive(c: Campaign, now: Date): boolean {
  if (!c.active) return false
  const day = localDay(now)
  if (c.startsOn && day < c.startsOn) return false
  if (c.endsOn && day > c.endsOn) return false
  if (c.days.length > 0 && !c.days.includes(now.getDay())) return false

  const from = minutesOf(c.startTime)
  const to = minutesOf(c.endTime)
  if (from !== null && to !== null && from !== to) {
    const t = now.getHours() * 60 + now.getMinutes()
    // Bitiş başlangıçtan küçükse gece yarısını geçiyor: 22.00-02.00.
    const inside = from < to ? t >= from && t < to : t >= from || t < to
    if (!inside) return false
  }
  return true
}

/** Satır kampanyanın kapsamında mı. Kategori ve ürün listesi boşsa bütün menü. */
export function covers(c: Campaign, line: OrderLine, categoryOf: (productId: string) => string | undefined) {
  if (c.categoryIds.length === 0 && c.productIds.length === 0) return true
  if (c.productIds.includes(line.productId)) return true
  const cat = categoryOf(line.productId)
  return cat !== undefined && c.categoryIds.includes(cat)
}

/**
 * Kampanyanın bu adisyondan düşeceği tutar; uymuyorsa 0.
 *
 * Tartılmayı bekleyen satırların tutarı henüz 0, hesaba bir şey katmıyorlar.
 */
export function campaignDiscount(
  c: Campaign,
  lines: OrderLine[],
  categoryOf: (productId: string) => string | undefined,
): number {
  const matched = lines.filter((l) => l.lineTotalKurus > 0 && covers(c, l, categoryOf))
  const sum = matched.reduce((s, l) => s + l.lineTotalKurus, 0)
  if (sum <= 0) return 0

  if (c.kind === 'percent') {
    if (!c.percent || c.percent <= 0) return 0
    if (c.minTotalKurus && sum < c.minTotalKurus) return 0
    return Math.min(sum, Math.round((sum * c.percent) / 100))
  }

  if (c.kind === 'amount') {
    if (!c.amountKurus) return 0
    if (c.minTotalKurus && sum < c.minTotalKurus) return 0
    return Math.min(sum, c.amountKurus)
  }

  // X al Y öde: birimleri pahalıdan ucuza diz, her X'lik grupta en ucuz
  // (X - Y) tanesi bedava, marketlerdeki gibi.
  const buy = c.buyQty ?? 0
  const pay = c.payQty ?? 0
  if (buy < 2 || pay < 1 || pay >= buy) return 0
  const units: number[] = []
  for (const l of matched) {
    // Tartılı satırda qty tabak sayısı; birim fiyatı tabak başına ortalama.
    const n = Math.max(0, Math.floor(l.qty))
    if (n === 0) continue
    const each = Math.round(l.lineTotalKurus / n)
    for (let i = 0; i < n; i++) units.push(each)
  }
  units.sort((a, b) => b - a)
  let free = 0
  for (let g = 0; g + buy <= units.length; g += buy) {
    for (let i = g + pay; i < g + buy; i++) free += units[i]
  }
  return free
}

/** Şu an bu adisyona uyan kampanyalar, en çok düşürenden başlayarak. */
export function campaignOffers(
  campaigns: Campaign[],
  lines: OrderLine[],
  categoryOf: (productId: string) => string | undefined,
  now: Date,
): CampaignOffer[] {
  return campaigns
    .filter((c) => isCampaignLive(c, now))
    .map((campaign) => ({ campaign, kurus: campaignDiscount(campaign, lines, categoryOf) }))
    .filter((o) => o.kurus > 0)
    .sort((a, b) => b.kurus - a.kurus || a.campaign.sortOrder - b.campaign.sortOrder)
}

const DAY_NAMES = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt']

/** "Hafta içi · 14.00-17.00 · 31 Eki'ye kadar" gibi koşul özeti; koşul yoksa boş. */
export function describeWhen(c: Campaign): string {
  const parts: string[] = []
  const days = [...c.days].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7))
  const key = days.join(',')
  if (key === '1,2,3,4,5') parts.push('Hafta içi')
  else if (key === '6,0') parts.push('Hafta sonu')
  else if (days.length > 0 && days.length < 7) parts.push(days.map((d) => DAY_NAMES[d]).join(', '))
  if (c.startTime && c.endTime && c.startTime !== c.endTime) {
    parts.push(`${c.startTime.replace(':', '.')}-${c.endTime.replace(':', '.')}`)
  }
  const fmt = (iso: string) =>
    new Date(`${iso}T12:00:00`).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })
  if (c.startsOn && c.endsOn) parts.push(`${fmt(c.startsOn)} - ${fmt(c.endsOn)}`)
  else if (c.endsOn) parts.push(`${fmt(c.endsOn)} tarihine kadar`)
  else if (c.startsOn) parts.push(`${fmt(c.startsOn)} itibarıyla`)
  return parts.join(', ')
}

/** "Tatlılarda %20", "500 ₺ üstüne 50 ₺", "3 al 2 öde" gibi kısa kural. */
export function describeRule(c: Campaign, scope: string): string {
  const where = scope ? `${scope}: ` : ''
  if (c.kind === 'percent') {
    const min = c.minTotalKurus ? ` (${formatTRY(c.minTotalKurus)} üstü)` : ''
    return `${where}%${c.percent ?? 0} indirim${min}`
  }
  if (c.kind === 'amount') {
    const min = c.minTotalKurus ? `${formatTRY(c.minTotalKurus)} üstüne ` : ''
    return `${where}${min}${formatTRY(c.amountKurus ?? 0)} indirim`
  }
  return `${where}${c.buyQty ?? 0} al ${c.payQty ?? 0} öde`
}

/**
 * Eklenen kampanyalar ve hala eklenebilecek olanlar.
 *
 * Eklenenler eklenme sırasıyla hesaplanıyor; her biri kendi kapsadığı
 * satırları alıyor, sonrakiler kalan satırlara bakıyor. Böylece içecekte
 * "3 al 2 öde" ile tatlıda "%20" birlikte geçerli, ama bir ürüne iki indirim
 * düşmüyor. Artık uymayan (ürün çıkarıldı, saat geçti) eklenen kendiliğinden
 * düşüyor.
 */
export function stackCampaigns(
  campaigns: Campaign[],
  appliedIds: string[],
  lines: OrderLine[],
  categoryOf: (productId: string) => string | undefined,
  now: Date,
): { applied: CampaignOffer[]; offers: CampaignOffer[] } {
  const live = campaigns.filter((c) => isCampaignLive(c, now))
  let rest = lines
  const applied: CampaignOffer[] = []
  for (const id of appliedIds) {
    const campaign = live.find((c) => c.id === id)
    if (!campaign) continue
    const kurus = campaignDiscount(campaign, rest, categoryOf)
    if (kurus <= 0) continue
    applied.push({ campaign, kurus })
    rest = rest.filter((l) => !covers(campaign, l, categoryOf))
  }
  const taken = new Set(applied.map((a) => a.campaign.id))
  const offers = campaignOffers(
    live.filter((c) => !taken.has(c.id)),
    rest,
    categoryOf,
    now,
  )
  return { applied, offers }
}
